import { useMemo, useState, useRef, useEffect } from 'react'
import { Button } from '../ui-kitNew/buttonNew'
import { EmptyState } from '../ui-kitNew/cardNew'
import { Segmented } from '../ui-kitNew/controlsNew'
import { BarChartIcon, LayoutIcon, PlusIcon, RotateIcon, TableIcon } from '../ui-kitNew/iconsNew'
import { FilterBar } from '../filtersNew/filter-barNew'
import { DataTable } from '../tableNew/data-tableNew.jsx'
import { WidgetCard } from './widget-cardNew'
import { WidgetEditor } from './widget-editorNew'
import Notification from '../common/notification/notification'
import { FIELDS } from '../../lib/reportNew/fieldsNew'
import { applyFilterState, createFilterState } from '../../lib/reportNew/filter-stateNew'
import { createWidget, nextWidgetId } from '../../lib/reportNew/widgetsNew'

function LoadingSkeleton({ view }) {
  const skeletonFilterBar = (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
      <div className="h-[24px] w-1/3 animate-pulse rounded-md bg-muted" />
      <div className="h-[24px] w-24 animate-pulse rounded-md bg-muted" />
    </div>
  )

  if (view === 'table') {
    return (
      <div className="flex flex-col gap-4">
        {skeletonFilterBar}
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
            >
              <div className="h-[14px] w-1/5 animate-pulse rounded bg-muted" />
              <div className="h-[14px] w-1/4 animate-pulse rounded bg-muted" />
              <div className="h-[14px] w-1/5 animate-pulse rounded bg-muted" />
              <div className="h-[14px] w-1/6 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {skeletonFilterBar}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
          <div className="h-[16px] w-1/2 animate-pulse rounded bg-muted" />
          <div className="h-[36px] w-2/3 animate-pulse rounded bg-muted" />
          <div className="h-[12px] w-1/3 animate-pulse rounded bg-muted" />
        </div>
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="h-[16px] w-1/3 animate-pulse rounded bg-muted" />
            <div className="h-[24px] w-24 animate-pulse rounded-md bg-muted" />
          </div>
          <div className="h-[280px] animate-pulse rounded-md bg-muted" />
        </div>
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
          <div className="h-[16px] w-1/2 animate-pulse rounded bg-muted" />
          <div className="h-[120px] animate-pulse rounded-full bg-muted" />
          <div className="h-[12px] w-1/3 animate-pulse rounded bg-muted" />
        </div>
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
          <div className="h-[16px] w-1/2 animate-pulse rounded bg-muted" />
          <div className="h-[36px] w-2/3 animate-pulse rounded bg-muted" />
          <div className="h-[12px] w-1/3 animate-pulse rounded bg-muted" />
        </div>
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="h-[16px] w-1/3 animate-pulse rounded bg-muted" />
            <div className="h-[24px] w-24 animate-pulse rounded-md bg-muted" />
          </div>
          <div className="h-[280px] animate-pulse rounded-md bg-muted" />
        </div>
      </div>
    </div>
  )
}

/**
 * Pantalla de informes: filtros globales + cuadro de mando personalizable
 * (añadir, editar, duplicar, reordenar y borrar gráficos) + tabla de detalle
 * con filtrado dinámico por columna. Los mismos filtros alimentan ambas vistas.
 */
export function ReportScreen({ rows, loading = false, widgets, setWidgets, onResetWidgets, saveStatus = 'saved' }) {
  const [filterState, setFilterState] = useState(() => createFilterState())
  const [view, setView] = useState('dashboard')
  const [editing, setEditing] = useState(null)
  const notificationRef = useRef(null)
  const prevSaveStatusRef = useRef('saved')

  useEffect(() => {
    const prev = prevSaveStatusRef.current
    prevSaveStatusRef.current = saveStatus

    if (prev !== 'saving') return

    if (saveStatus === 'saved') {
      notificationRef.current?.addNotification('Gráfico guardado correctamente', 'success')
    } else if (saveStatus === 'error') {
      notificationRef.current?.addNotification('Error al guardar el gráfico', 'error')
    }
  }, [saveStatus])

  const filteredRows = useMemo(
    () => applyFilterState(rows ?? [], filterState, FIELDS),
    [rows, filterState]
  )

  const updateWidget = (next) => {
    setWidgets((prev) => {
      const exists = prev.some((widget) => widget.id === next.id)
      const clean = { ...next }
      delete clean.__isNew
      return exists ? prev.map((widget) => (widget.id === next.id ? clean : widget)) : [...prev, clean]
    })
    setEditing(null)
  }

  const moveWidget = (id, offset) => {
    setWidgets((prev) => {
      const index = prev.findIndex((widget) => widget.id === id)
      const target = index + offset
      if (index < 0 || target < 0 || target >= prev.length) return prev
      const copy = [...prev]
      const [item] = copy.splice(index, 1)
      copy.splice(target, 0, item)
      return copy
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] leading-tight font-semibold tracking-tight text-foreground">
            Informes Personalizados
          </h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Construye tu propio cuadro de mando y explora el detalle con filtros dinámicos.
          </p>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <Segmented
            className="w-full sm:w-auto"
            stretch
            size="md"
            ariaLabel="Cambiar de vista"
            value={view}
            onChange={setView}
            options={[
              { value: 'dashboard', label: 'Cuadro de mando', icon: <LayoutIcon size={15} /> },
              { value: 'table', label: 'Detalle de datos', icon: <TableIcon size={15} /> },
            ]}
          />
          {view === 'dashboard' && (
            <>
              {onResetWidgets && (
                <Button
                  className="flex-1 sm:flex-none"
                  variant="outline"
                  size="lg"
                  onClick={onResetWidgets}
                >
                  <RotateIcon size={15} />
                  Restablecer
                </Button>
              )}
              <Button
                className="flex-1 sm:flex-none"
                variant="primary"
                size="lg"
                onClick={() => setEditing({ ...createWidget(), __isNew: true })}
              >
                <PlusIcon size={16} />
                Añadir gráfico
              </Button>
            </>
          )}
        </div>
      </header>

      {loading ? (
        <LoadingSkeleton view={view} />
      ) : (
        <>
          <FilterBar
            rows={rows}
            state={filterState}
            onChange={setFilterState}
            resultCount={filteredRows.length}
            totalCount={(rows ?? []).length}
          />

          {view === 'dashboard' ? (
            widgets.length === 0 ? (
              <EmptyState
                icon={<BarChartIcon size={26} />}
                title="Tu informe está vacío"
                description="Añade el primer gráfico eligiendo qué métrica quieres medir y cómo agruparla."
                action={
                  <Button
                    variant="primary"
                    onClick={() => setEditing({ ...createWidget(), __isNew: true })}
                  >
                    <PlusIcon size={16} />
                    Añadir gráfico
                  </Button>
                }
              />
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {widgets.map((widget, index) => (
                  <WidgetCard
                    key={widget.id}
                    widget={widget}
                    rows={filteredRows}
                    isFirst={index === 0}
                    isLast={index === widgets.length - 1}
                    onEdit={() => setEditing(widget)}
                    onMove={(offset) => moveWidget(widget.id, offset)}
                    onDuplicate={() =>
                      setWidgets((prev) => {
                        const copy = [...prev]
                        copy.splice(index + 1, 0, {
                          ...widget,
                          id: nextWidgetId(),
                          title: `${widget.title} (copia)`,
                        })
                        return copy
                      })
                    }
                    onRemove={() => setWidgets((prev) => prev.filter((item) => item.id !== widget.id))}
                  />
                ))}
              </div>
            )
          ) : (
            <DataTable rows={filteredRows} fields={FIELDS} />
          )}
        </>
      )}

      <WidgetEditor
        open={Boolean(editing)}
        widget={editing}
        rows={filteredRows}
        onClose={() => setEditing(null)}
        onSave={updateWidget}
      />
      <Notification ref={notificationRef} />
    </div>
  )
}
